/**
 * Pure data builders for prePopulatedData payloads — see {@link PrePopulatedData}
 * for the contract consumed by the prepopulation transforms.
 *
 * On-request stops are excluded from master-specific fields (sequenceOrder,
 * assignedConfiguredServiceEventIds, newMasterStops) but kept in daily-specific
 * fields (dailyPositionMap, allDailyStopsGrouped) so they still get their
 * daily occurrence prepopulated.
 */
import { groupBy, chunk } from 'lodash'

import { getNewlyAssignedRows as getNewlyAssignedRowsRaw } from 'components/route-manager/BulkActions/settings'
import { formatDateToBEFormatDateFns } from 'utils/date'
// JSDoc-inferred types from these JS modules don't match DialogRow; loose
// signatures below let call-site typing govern instead.
import { handleError } from 'utils/error'
import { isOnRequestStop as isOnRequestStopRaw } from 'utils/route'
import { hasRecurrenceOnDate as hasRecurrenceOnDateRaw } from 'utils/routeStopOperations/recurrence'

import type { IsoDateString } from 'types/entities/shared'
import type {
  SequenceEditPrePopulatedData,
  PendingDailyStop,
  PendingMasterStop,
  UnassignPrePopulatedData,
} from 'types/modules/routing/prePopulatedData'

const getNewlyAssignedRows = getNewlyAssignedRowsRaw as (rows: ReadonlyArray<unknown>, destinationRouteId: string) => unknown[]
const isOnRequestStop = isOnRequestStopRaw as (stop: unknown) => boolean
const hasRecurrenceOnDate = hasRecurrenceOnDateRaw as (stop: unknown, date: Date | string) => boolean

const PREVIEW_BATCH_SIZE = 500

/**
 * Loose row shape callers pass in. None of the entry points (form rows,
 * cloned preview rows, MUI Grid rows) guarantee a full
 * MasterStopApi/DailyStopApi shape — the rows are flat-denorm bags. Tightening
 * this is the prefill follow-up.
 */
export type DialogRow = Partial<PendingMasterStop & PendingDailyStop>

interface PreviewResponse {
  stops?: PendingDailyStop[]
}

interface RouteStopsPreviewArgs {
  stops: string[]
  serviceDate: string
  updateMasterSequence: boolean
}

/**
 * RTKQ `useLazy*Query` returns a trigger fn whose call resolves to a
 * QueryActionCreatorResult with `.unwrap()`. Typing the trigger inline so
 * builders don't need to import the generated RTKQ types directly.
 */
export type RouteStopsPreviewTrigger = (args: RouteStopsPreviewArgs) => { unwrap: () => Promise<PreviewResponse> }

/**
 * Build prePopulatedData for the unassign "Edit master sequence" flow.
 *
 * Fetches the route stops preview to discover which daily service dates have
 * occurrences of the stops being unassigned. Without this, the master sequence
 * editor won't render daily grids, so the publish pipeline will skip daily
 * unassignment entirely.
 */
export const buildUnassignPrepopulationData = async (
  moveToRows: ReadonlyArray<DialogRow>,
  serviceDate: Date,
  getRouteStopsPreview: RouteStopsPreviewTrigger | null | undefined
): Promise<UnassignPrePopulatedData> => {
  const recurringRows = moveToRows.filter(row => !isOnRequestStop(row))
  const unassignedConfiguredServiceEventIds = recurringRows
    .map(row => row.configuredServiceEventId)
    .filter((id): id is string => Boolean(id))

  // On-request stops belong to daily but not master.
  const unassignedDailyConfiguredServiceEventIds = moveToRows
    .map(row => row.configuredServiceEventId)
    .filter((id): id is string => Boolean(id))
  const workOrderIds = moveToRows.map(row => row.workOrderId ?? row.id).filter((id): id is string => Boolean(id))

  let allNewServiceDates: IsoDateString[] = []

  if (workOrderIds.length > 0 && getRouteStopsPreview) {
    const batches = chunk(workOrderIds, PREVIEW_BATCH_SIZE)
    const serviceDateStr = formatDateToBEFormatDateFns(serviceDate)

    const results = await Promise.all(
      batches.map(batch =>
        getRouteStopsPreview({ stops: batch, serviceDate: serviceDateStr, updateMasterSequence: true }).unwrap().catch(handleError)
      )
    )

    const allPreviewStops = results.flatMap(response => response?.stops ?? [])
    if (allPreviewStops.length > 0) {
      allNewServiceDates = Array.from(new Set<IsoDateString>(allPreviewStops.map(s => s.serviceDate)))
    }
  }

  return {
    unassignedConfiguredServiceEventIds,
    unassignedDailyConfiguredServiceEventIds,
    allNewServiceDates,
  }
}

/**
 * Build prePopulatedData for the bulk resequence flow. Single-stop resequence
 * callers dispatch {@link ResequencePrePopulatedData} directly instead.
 *
 * Returns the master-only subset of {@link SequenceEditPrePopulatedData} —
 * daily-side fields are intentionally omitted so the transforms leave the
 * daily grids untouched (the user adjusts those on the master sequence page).
 */
export const buildResequencePrepopulationData = (rows: ReadonlyArray<DialogRow>): SequenceEditPrePopulatedData => ({
  sequenceOrder: rows
    .filter(row => !isOnRequestStop(row))
    .map(row => row.configuredServiceEventId)
    .filter((id): id is string => Boolean(id)),
})

/** Build prePopulatedData for the assign/move "Edit master sequence" flow. */
export const buildSequenceEditPrepopulationData = async (
  moveToRows: ReadonlyArray<DialogRow>,
  destinationRouteId: string,
  serviceDate: Date,
  getRouteStopsPreview: RouteStopsPreviewTrigger | null | undefined,
  // Name of the route the stops were moved from — stamped onto the moved rows so
  // the master-sequence editor renders the cross-route chip ("From route · #N").
  sourceRouteName?: string | null
): Promise<SequenceEditPrePopulatedData> => {
  const recurringRows = moveToRows.filter(row => !isOnRequestStop(row))
  // Deduplicate CSE IDs — keep first occurrence to preserve the intended position.
  const seen = new Set<string>()
  const sequenceOrder = recurringRows
    .map(row => row.configuredServiceEventId)
    .filter((id): id is string => {
      if (!id || seen.has(id)) return false
      seen.add(id)
      return true
    })

  // Tag each moved-in row with its origin route. `routeId` on the row is the
  // source route (these rows came from a different route than the destination);
  // `sourceRouteName` is the human label for the chip. Stamped here so it rides
  // through to both the master grid (newMasterStops) and the daily grids (CS-2918).
  const stampSource = <T extends DialogRow>(row: T): T => ({
    ...row,
    sourceRouteId: (row as { routeId?: string | null }).routeId ?? null,
    ...(sourceRouteName ? { sourceRouteName } : {}),
  })
  const assignedRows = (getNewlyAssignedRows(moveToRows, destinationRouteId) as DialogRow[]).map(stampSource)
  const recurringAssignedRows = assignedRows.filter(row => !isOnRequestStop(row))
  // CSE → source-route-id for the moved set, so preview rows on future dailies
  // (which the BE returns without origin metadata) can be re-stamped below.
  const sourceRouteIdByCse = new Map(
    assignedRows.map(row => [row.configuredServiceEventId, (row as { routeId?: string | null }).routeId ?? null])
  )
  const assignedConfiguredServiceEventIds = recurringAssignedRows
    .map(row => row.configuredServiceEventId)
    .filter((id): id is string => Boolean(id))

  // Daily side keeps on-request rows; master skips them.
  const assignedDailyConfiguredServiceEventIds = assignedRows
    .map(row => row.configuredServiceEventId)
    .filter((id): id is string => Boolean(id))
  const allAssignedWorkOrderIds = assignedRows.map(row => row.workOrderId).filter((id): id is string => Boolean(id))

  // Key by both workOrderId AND configuredServiceEventId. Current-day preview
  // matches by woId; future-day occurrences have different woIds (one per
  // recurrence) so the CSE fallback preserves the user's arrangement there.
  const dailyPositionMap: Record<string, number> = {}
  moveToRows.forEach((row, index) => {
    const position = index + 1
    const woKey = row.workOrderId ?? row.id
    if (woKey) dailyPositionMap[woKey] = position
    const cseKey = row.configuredServiceEventId
    if (cseKey) dailyPositionMap[cseKey] = position
  })

  let allDailyStopsGrouped: Record<IsoDateString, PendingDailyStop[]> = {}
  let allNewServiceDates: IsoDateString[] = []

  if (allAssignedWorkOrderIds.length > 0 && getRouteStopsPreview) {
    const batches = chunk(allAssignedWorkOrderIds, PREVIEW_BATCH_SIZE)
    const serviceDateStr = formatDateToBEFormatDateFns(serviceDate)

    const results = await Promise.all(
      batches.map(batch =>
        getRouteStopsPreview({ stops: batch, serviceDate: serviceDateStr, updateMasterSequence: true }).unwrap().catch(handleError)
      )
    )

    // Re-stamp the BE preview rows for the moved stops with their origin route
    // so future-week daily grids render the same cross-route chip as the master.
    const stampPreviewSource = (stop: PendingDailyStop): PendingDailyStop =>
      sourceRouteIdByCse.has(stop.configuredServiceEventId)
        ? {
            ...stop,
            sourceRouteId: sourceRouteIdByCse.get(stop.configuredServiceEventId) ?? null,
            ...(sourceRouteName ? { sourceRouteName } : {}),
          }
        : stop
    const allPreviewStops = results.flatMap(response => response?.stops ?? []).map(stampPreviewSource)

    if (allPreviewStops.length > 0) {
      allDailyStopsGrouped = groupBy(allPreviewStops, s => s.serviceDate) as Record<IsoDateString, PendingDailyStop[]>
      allNewServiceDates = Object.keys(allDailyStopsGrouped)
    }
  }

  const currentServiceDateStr = formatDateToBEFormatDateFns(serviceDate)
  if (assignedRows.length > 0) {
    const existingWoIdsForDate = new Set<string>(
      (allDailyStopsGrouped[currentServiceDateStr] ?? []).map(s => s.workOrderId).filter((id): id is string => Boolean(id))
    )
    const missingForDate = assignedRows.filter(s => {
      const woId = s.workOrderId
      // workOrderId may be undefined on master-source rows; treat undefined as
      // "not in existing" (truthful) and let hasRecurrenceOnDate gate inclusion.
      return (woId === undefined || !existingWoIdsForDate.has(woId)) && hasRecurrenceOnDate(s, serviceDate)
    })
    if (missingForDate.length > 0) {
      allDailyStopsGrouped = {
        ...allDailyStopsGrouped,
        [currentServiceDateStr]: [
          ...(allDailyStopsGrouped[currentServiceDateStr] ?? []),
          // Loose DialogRows may not have every DailyStopApi field; the display
          // layer reads the subset available. Tightening is the prefill follow-up.
          ...(missingForDate as unknown as PendingDailyStop[]),
        ],
      }
    }
    if (!allNewServiceDates.includes(currentServiceDateStr)) {
      allNewServiceDates = [...allNewServiceDates, currentServiceDateStr]
    }
  }

  return {
    sequenceOrder,
    assignedConfiguredServiceEventIds,
    assignedDailyConfiguredServiceEventIds,
    // Loose-row caveat as above — display reads whatever flat fields the
    // caller supplies; missing fields render blank.
    newMasterStops: recurringAssignedRows as unknown as PendingMasterStop[],
    allNewServiceDates,
    allDailyStopsGrouped,
    dailyPositionMap,
  }
}
